import { get } from "lodash";
import {
  ACTIVITY_TYPES,
  ALVIO_ACTIVITY_ACTIONS,
  LOGS_STATUSES_KEYS,
  SUBSCRIPTION_STATUSES,
  TYPES,
} from "utils/constants";

const quoteLikeUserActivityActions = new Set([
  "addQuote",
  "sendQuote",
  "generateQuoteDocuments",
]);

export const sourceUserActivityActions = new Set([
  "projectCreatedFromSource",
  "projectSourceChanged",
]);

const CONTACT_USER_FROM_LOG_PATHS = [
  "data.result.project.contact.user",
  "data.result.quote.project.contact.user",
  "data.result.subscription.project.contact.user",
  "data.result.contact.user",
  "data.input.data.project.contact.user",
  "data.input.data.contact.user",
  "data.input.contact.user",
  "data.input.project.contact.user",
  "data.contact.user",
];

const extractContactUserFromUserActivityLog = (log) => {
  for (const path of CONTACT_USER_FROM_LOG_PATHS) {
    const user = get(log, path);
    if (user && (user.firstname || user.lastname)) {
      return {
        firstname: user.firstname || "",
        lastname: user.lastname || "",
      };
    }
  }
  return undefined;
};

const resolveStatusFromUserActivityLog = (log, statusPath) => {
  const fromPath = get(log, statusPath);
  if (fromPath != null && fromPath !== "") {
    return fromPath;
  }
  if (quoteLikeUserActivityActions.has(log.action)) {
    return SUBSCRIPTION_STATUSES.DRAFT;
  }
  return log.action;
};

const buildDashboardMetaFromLog = (log) => {
  const projectFromResult = get(log, "data.result.project");
  const projectIdInput =
    get(log, "data.projectId") ||
    get(log, "data.input.projectId") ||
    get(log, "data.input.project.id") ||
    get(log, "data.input.data.projectId") ||
    get(log, "data.input.data.project.id");
  let project =
    projectFromResult || (projectIdInput ? { id: projectIdInput } : null);
  const contactId =
    get(log, "data.result.project.contact.id") ||
    get(log, "data.result.contact.id") ||
    get(log, "data.input.contactId") ||
    get(log, "data.input.data.contact.id") ||
    get(log, "data.input.data.contactId") ||
    get(log, "data.input.contact.id") ||
    get(log, "data.contact.id") ||
    null;

  if (project && contactId && !get(project, "contact.id")) {
    project = { ...project, contact: { id: contactId } };
  }

  if (!get(project, "id") && !contactId) {
    return null;
  }
  return { project, contactId };
};

const resolveDashboardActivityDate = (log) => {
  const baseDate = log.updatedDate || log.createdDate;
  if (log.action === "electronicSignatureCompleted") {
    return get(log, "data.signedDate") || baseDate;
  }
  if (log.action === "manualSignatureCompleted") {
    return (
      get(log, "data.initiator.date") || get(log, "data.signedDate") || baseDate
    );
  }
  return baseDate;
};

/**
 * Maps a UserActivity row (dashboard feed) to the unified activity shape used
 * by ActivitiesLog / contact drawer items.
 */
const mapUserActivityToBrokerActivity = (log) => {
  const date = resolveDashboardActivityDate(log);
  const author = log.user;
  const dashboardMeta = buildDashboardMetaFromLog(log);
  const contactUser = extractContactUserFromUserActivityLog(log);

  if (Object.values(ALVIO_ACTIVITY_ACTIONS).includes(log.action)) {
    return {
      activityType: ACTIVITY_TYPES.WHATSAPP,
      status: log.action,
      date: get(log, "data.initiator.date") || date,
      data: log.data,
      dashboardMeta,
      contactUser,
    };
  }

  if (log.module === "EMAIL" && log.action === "sendEmail") {
    return {
      activityType: ACTIVITY_TYPES.EMAIL_SENT,
      date,
      emailActivity: {
        id: log.id,
        subject: get(log, "data.input.subject", ""),
        to: get(log, "data.input.to", []) || [],
        cc: get(log, "data.input.cc"),
        user: log.user,
      },
      dashboardMeta,
      contactUser,
    };
  }

  if (
    log.action === "projectDistributed" ||
    log.action === "projectTakenOver"
  ) {
    const advisorName =
      log.action === "projectDistributed"
        ? `${get(log, "data.distributedTo.firstname", "")} ${get(log, "data.distributedTo.lastname", "")}`.trim()
        : `${get(log, "data.takenOverBy.firstname", "")} ${get(log, "data.takenOverBy.lastname", "")}`.trim();

    return {
      activityType: ACTIVITY_TYPES.STATUS,
      status: log.action,
      date,
      author: { ...author, displayName: advisorName },
      dashboardMeta: {
        project: { id: get(log, "data.projectId") },
      },
      contactUser,
    };
  }

  if (sourceUserActivityActions.has(log.action)) {
    const initiator = get(log, "data.initiator") || undefined;
    return {
      activityType: ACTIVITY_TYPES.STATUS,
      status: log.action,
      date: get(initiator, "date") || date,
      initiator,
      author,
      data: log.data,
      dashboardMeta,
      contactUser,
    };
  }

  if (log.action === "addQualification") {
    const status = get(log, LOGS_STATUSES_KEYS.addQualification, "");
    return {
      activityType: ACTIVITY_TYPES.QUALIFICATION,
      status,
      date,
      author,
      dashboardMeta,
      contactUser,
    };
  }

  const statusPath = LOGS_STATUSES_KEYS[log.action];
  if (statusPath) {
    const statusRaw = resolveStatusFromUserActivityLog(log, statusPath);
    return {
      activityType: ACTIVITY_TYPES.STATUS,
      status: statusRaw,
      date,
      type: TYPES.DANGER,
      author,
      dashboardMeta,
      contactUser,
    };
  }

  return {
    activityType: ACTIVITY_TYPES.STATUS,
    status: log.action,
    date,
    type: TYPES.DANGER,
    author,
    dashboardMeta,
    contactUser,
  };
};

export default mapUserActivityToBrokerActivity;
